import React, {
  FC,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import styled from 'styled-components';
import { HiChevronLeft, HiChevronRight } from 'react-icons/hi2';

import { AccountContext } from '@/features/account-context';
import {
  fetchNewestConnectedStores,
  fillWithConnectedStores,
  getRecentStores,
  MAX_RECENT_STORES,
  RecentStore,
} from '@/utils/recent-stores';
import { routes } from '@/webapi/routes';
import { setActiveAlias } from '@/wrapPageElelement';

const SCROLL_STEP = 150;
const FALLBACK_IMAGE = `https://sdk.loomi-prod.xyz/VSLY/not_found.png`;

function stripProtocolAndWww(domain: string) {
  return domain?.replace(`https://`, ``)?.replace(`www.`, ``);
}

async function selectStore(alias: string) {
  await fetch(routes.selectAccount(), {
    method: `POST`,
    credentials: `include`,
    headers: {
      'Content-Type': `application/json`,
    },
    body: JSON.stringify({ storeAlias: alias }),
  });
  setActiveAlias(alias);
  const url = new URL(window.location.href);
  url.searchParams.set(`al`, alias);
  window.location.replace(url.toString());
}

export const RecentStoresList: FC = () => {
  const ctx = useContext(AccountContext);
  const currentAlias = ctx?.account?.store?.alias;
  const scrollRef = useRef<HTMLDivElement>(null);

  const [recentStores, setRecentStores] = useState<RecentStore[]>([]);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  useEffect(() => {
    const stored = getRecentStores();
    setRecentStores(stored);
    if (stored.length >= MAX_RECENT_STORES) return undefined;
    let cancelled = false;
    fetchNewestConnectedStores().then((connected) => {
      if (!cancelled) {
        setRecentStores(fillWithConnectedStores(stored, connected));
      }
    });
    return () => {
      cancelled = true;
    };
  }, []);

  const updateScrollState = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    setCanScrollLeft(el.scrollLeft > 0);
    setCanScrollRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 1);
  }, []);

  useEffect(() => {
    updateScrollState();
    window.addEventListener(`resize`, updateScrollState);
    return () => window.removeEventListener(`resize`, updateScrollState);
  }, [recentStores, updateScrollState]);

  const scrollBy = (delta: number) => {
    scrollRef.current?.scrollBy({ left: delta, behavior: `smooth` });
  };

  if (recentStores.length <= 1) {
    return null;
  }

  return (
    <Wrapper>
      {canScrollLeft && (
        <ArrowButton
          className="left"
          type="button"
          onClick={() => scrollBy(-SCROLL_STEP)}
        >
          <HiChevronLeft size={16} />
        </ArrowButton>
      )}
      <EdgeMask className="left" $show={canScrollLeft} />
      <CardsRow ref={scrollRef} onScroll={updateScrollState}>
        {recentStores.map((store) => (
          <StoreCard
            key={store.alias}
            type="button"
            className={store.alias === currentAlias ? `active` : ``}
            onClick={() => selectStore(store.alias)}
          >
            <StoreImage
              src={routes.prodStoreSnapshotUrl(store.alias)}
              alt={store.domain}
              onError={({ currentTarget }) => {
                currentTarget.onerror = null;
                currentTarget.src = FALLBACK_IMAGE;
              }}
            />
            <StoreDomainLabel>
              {stripProtocolAndWww(store.domain)}
            </StoreDomainLabel>
          </StoreCard>
        ))}
      </CardsRow>
      <EdgeMask className="right" $show={canScrollRight} />
      {canScrollRight && (
        <ArrowButton
          className="right"
          type="button"
          onClick={() => scrollBy(SCROLL_STEP)}
        >
          <HiChevronRight size={16} />
        </ArrowButton>
      )}
    </Wrapper>
  );
};

const ArrowButton = styled.button`
  position: absolute;
  top: calc(50% - 1.5rem);
  transform: translateY(-50%);
  z-index: 1;

  width: 3rem;
  height: 3rem;
  display: flex;
  align-items: center;
  justify-content: center;

  background: white;
  border: none;
  border-radius: 50%;
  box-shadow: 0 2px 6px 0 rgba(0, 0, 0, 0.15);
  cursor: pointer;

  opacity: 0;
  pointer-events: none;
  transition: opacity 0.15s ease-out;

  &&.left {
    left: -1.2rem;
  }

  &&.right {
    right: -1.2rem;
  }
`;

const Wrapper = styled.div`
  position: relative;
  width: 100%;
  min-width: 0;
  margin-top: 0.8rem;
  margin-bottom: -0.8rem;

  &&:hover ${ArrowButton} {
    opacity: 1;
    pointer-events: auto;
  }
`;

const CardsRow = styled.div`
  display: flex;
  flex-direction: row;
  gap: 2rem;

  width: 100%;
  min-width: 0;
  padding: 0.8rem 0 1rem;
  overflow-x: auto;
  scroll-behavior: smooth;

  scrollbar-width: none;
  &&::-webkit-scrollbar {
    display: none;
  }
`;

const EdgeMask = styled.div<{ $show: boolean }>`
  position: absolute;
  top: 0.8rem;
  height: 12rem;
  width: 3rem;
  z-index: 1;

  opacity: ${({ $show }) => ($show ? 1 : 0)};
  transition: opacity 0.15s ease-out;
  pointer-events: none;

  &&.left {
    left: 0;
    background: linear-gradient(to right, white, transparent);
  }

  &&.right {
    right: 0;
    background: linear-gradient(to left, white, transparent);
  }
`;

const StoreImage = styled.img`
  width: 9rem;
  height: 12rem;
  object-fit: cover;
  object-position: top;
  border-radius: 1.2rem;
  border: 1px solid transparent;
  transition: box-shadow 0.2s ease-out;
`;

const StoreDomainLabel = styled.span`
  font-size: 1.1rem;
  color: #a6afb8;
  font-weight: 300;
  max-width: 9rem;
  text-align: center;
  word-break: break-word;
`;

const StoreCard = styled.button`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1rem;

  flex: 0 0 auto;
  width: 9rem;

  background: none;
  border: none;
  cursor: pointer;
  padding: 0;

  transition: transform 0.2s ease-out;

  ${StoreImage} {
    border-color: rgb(65 65 65 / 0.2);
  }

  &&:hover {
    transform: translateY(-0.4rem);

    ${StoreImage} {
      box-shadow: 0 1rem 1.5rem 0 rgba(0, 0, 0, 0.18);
    }
  }

  &&.active {
    ${StoreImage} {
      border-color: #414141;
      border-width: 2px;
    }

    ${StoreDomainLabel} {
      color: #414141;
      font-weight: 600;
    }
  }
`;
